import { ConfirmAction } from "./ConfirmAction";
import { EditableName } from "./EditableName";
import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { AppView } from "./AppShell";
import { AccountAuth } from "./AccountAuth";
import { Monitor, ArrowRight, Copy, FolderGit2, Check } from "lucide-react";
import { agentSetupPrompt } from "../../src/distribution/setup-prompt";

export type Subscription = {
  linked: boolean;
  status?: "active" | "inactive";
  checkout?: "open" | "processing";
  licenseKey?: string;
  email?: string;
  expiresAt?: string;
  machines?: { devices?: Array<{ device_id: string; revoked: number; label?: string | null }> };
};
export type AccountSubscriptionState = {
  subject: string;
  subscription: Subscription | null;
};
const pendingKey = "phasedrift.purchase-handoff";
export async function accountRequest<T>(
  path: string,
  body: unknown = {},
  accessToken?: string,
): Promise<T> {
  const response = await fetch("/api/account/" + path, {
    method: "POST",
    signal: AbortSignal.timeout(15000),
    headers: {
      "content-type": "application/json",
      ...(accessToken ? { authorization: "Bearer " + accessToken } : {}),
    },
    body: JSON.stringify(body),
  });
  const value = await response.json();
  if (!response.ok) throw Error(value.error ?? "account_unavailable");
  return value;
}
export type ComputerApprovalState = "pending" | "approved" | null;
export function readAccountFlow() {
  const fragment = new URLSearchParams(location.hash.slice(1)),
    purchase = fragment.get("purchase"),
    device = fragment.get("device");
  if (purchase && /^[a-f0-9]{64}$/.test(purchase)) {
    sessionStorage.setItem(pendingKey, purchase);
    history.replaceState(
      history.state,
      "",
      location.pathname + location.search,
    );
  }
  return {
    purchase: sessionStorage.getItem(pendingKey),
    device: device && /^[A-F0-9]{10}$/.test(device) ? device : null,
  };
}
export function AccountSetup({
  flow,
  approvalState,
  onApprovalStateChange,
  subject,
  getClient,
  onConnected,
  onSubscriptionChange,
  view,
  recovery,
  onPasswordSaved,
  navigate,
}: {
  flow: ReturnType<typeof readAccountFlow>;
  approvalState: ComputerApprovalState;
  onApprovalStateChange: (state: ComputerApprovalState) => void;
  view: AppView;
  recovery: boolean;
  onPasswordSaved: () => void;
  navigate: (view: AppView) => void;
  subject: string | null | undefined;
  getClient: () => Promise<SupabaseClient>;
  onConnected: () => void;
  onSubscriptionChange: (state: AccountSubscriptionState) => void;
}) {
  const [email, setEmail] = useState(""),
    [busy, setBusy] = useState(false),
    [note, setNote] = useState(""),
    [subscription, setSubscription] = useState<Subscription | null>(null),
    [visible, setVisible] = useState(false),
    [historyApproval,setHistoryApproval] = useState<{history:boolean;deviceLabel:string|null}|null>(null);
  const [refresh, setRefresh] = useState(0);
  const [releaseComputer, setReleaseComputer] = useState<{id:string;name:string}|null>(null);
  const epoch = useRef(0);
  const approvalStateRef = useRef(approvalState);
  approvalStateRef.current = approvalState;
  const approvalHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (subject && !recovery && approvalState) approvalHeading.current?.focus();
  }, [subject, recovery, approvalState]);
  useEffect(() => {
    const run = ++epoch.current;
    let timer: ReturnType<typeof setTimeout> | undefined, checking = false, lastCheck = 0, polls = 0, waiting = false;
    setSubscription(null);setReleaseComputer(null);
    if (subject) onSubscriptionChange({ subject, subscription: null });
    setHistoryApproval(null);setVisible(false);setBusy(false);setNote("");
    const check = async () => {
      if(checking || run !== epoch.current)return;
      checking=true;lastCheck=Date.now();clearTimeout(timer);
      try {
        if (!subject) {
          if(flow.purchase){const info=await accountRequest<{email:string}>("purchase",{purchase:flow.purchase});if(run===epoch.current)setEmail(info.email);}
          return;
        }
        const client=await getClient(),{data}=await client.auth.getSession();
        if(!data.session)return;
        if(flow.purchase&&sessionStorage.getItem(pendingKey)){
          try{await accountRequest("connect",{purchase:flow.purchase},data.session.access_token);}
          catch(error){if(!(error instanceof Error&&error.message==='purchase_expired'))throw error;}
          if(run!==epoch.current)return;
          sessionStorage.removeItem(pendingKey);onConnected();
        }
        const value=await accountRequest<Subscription>("subscription",{},data.session.access_token);
        if(!value||typeof value.linked!=="boolean"||(value.linked&&value.status!=="active"&&value.status!=="inactive")||
           (value.checkout!==undefined&&value.checkout!=='open'&&value.checkout!=='processing'))throw Error('invalid_subscription_response');
        if(run!==epoch.current)return;
        waiting=Boolean(value.checkout);setSubscription(value);onSubscriptionChange({subject,subscription:value});setNote("");setEmail(data.session.user.email??"");
        if(value.linked&&value.status==='active'){
          const url=new URL(location.href);if(url.searchParams.has('checkout')||url.searchParams.has('onboarding')){
            url.searchParams.delete('checkout');url.searchParams.delete('onboarding');history.replaceState(history.state,'',url.pathname+url.search+url.hash);
          }
          if(flow.device && approvalStateRef.current==='pending'){
            try {
              const intent=await accountRequest<{history:boolean;deviceLabel:string|null}>("device-info",{userCode:flow.device},data.session.access_token);
              if(run===epoch.current && approvalStateRef.current==='pending')setHistoryApproval(intent);
            } catch {
              if(run===epoch.current && approvalStateRef.current==='pending')setNote('This computer approval could not be loaded. Return to your terminal to request a new approval link.');
            }
          }
        }else if(value.checkout){
          // No manual refresh in the purchase path. Visible tabs reconcile the
          // durable receipt automatically; focus also catches another tab's payment.
          timer=setTimeout(()=>{if(document.visibilityState==='visible')void check();},++polls<=20?10000:30000);
        }
      }catch(error){
        if(run!==epoch.current)return;
        const message=error instanceof Error?error.message:'';
        setNote(message==='checkout_email_mismatch'?'This older purchase belongs to a different email. Sign in with its checkout email.':
          message==='purchase_ambiguous'||message==='account_link_failed'?'We found a purchase that needs an account ownership check. Contact hello@phasedrift.ai before purchasing again.':
          'We couldn’t confirm your subscription right now. Your purchase is safe. Try again in a moment.');
        if(waiting||new URLSearchParams(location.search).get('checkout')==='complete')timer=setTimeout(()=>{if(document.visibilityState==='visible')void check();},30000);
      }finally{checking=false;}
    };
    const onFocus=()=>{if(document.visibilityState==='visible'&&Date.now()-lastCheck>=5000)void check();};
    window.addEventListener('focus',onFocus);document.addEventListener('visibilitychange',onFocus);
    void check();
    return ()=>{epoch.current++;clearTimeout(timer);window.removeEventListener('focus',onFocus);document.removeEventListener('visibilitychange',onFocus);};
  }, [subject,refresh]);
  async function authenticated<T = { url?: string; connected?: boolean }>(
    path: string,
    body: unknown = {},
  ) {
    const client = await getClient(),
      { data } = await client.auth.getSession();
    if (!data.session) throw Error();
    return accountRequest<T>(path, body, data.session.access_token);
  }
  async function renameComputer(deviceId: string, label: string) {
    const owner=epoch.current;
    await authenticated("rename-device",{deviceId,label});
    if(owner!==epoch.current)throw Error("owner_changed");
    const next=await authenticated<Subscription>("subscription");
    if(owner!==epoch.current)throw Error("owner_changed");
    setSubscription(next);onSubscriptionChange({subject:subject!,subscription:next});
  }
  async function releaseSelectedComputer() {
    if(!releaseComputer)return;
    const owner=epoch.current;setBusy(true);
    try {
      const released=await authenticated<{revoked:boolean}>("release-device",{deviceId:releaseComputer.id,confirmation:"RELEASE COMPUTER"});
      if(owner!==epoch.current)return;
      if(released.revoked!==true)throw Error("release_not_confirmed");
      if(subscription){const next={...subscription,machines:{...subscription.machines,devices:subscription.machines?.devices?.map(device=>device.device_id===releaseComputer.id?{...device,revoked:1}:device)}};setSubscription(next);onSubscriptionChange({subject:subject!,subscription:next});}
      setNote("Computer released. You can connect a replacement.");
      try {
        const value=await authenticated<Subscription>("subscription");
        if(owner===epoch.current){setSubscription(value);onSubscriptionChange({subject:subject!,subscription:value});}
      }catch{if(owner===epoch.current)setNote("Computer released. The subscription list could not be refreshed; reload to check the latest list.");}
    }catch{if(owner===epoch.current)setNote("Computer could not be released. Try again.");}
    finally{if(owner===epoch.current){setBusy(false);setReleaseComputer(null);}}
  }
  async function manageBilling() {
    const owner = epoch.current;
    setBusy(true);
    try {
      const data = await authenticated("portal");
      if (owner !== epoch.current) return;
      if (!data.url?.startsWith("https://billing.stripe.com/")) throw Error();
      location.assign(data.url);
    } catch {
      if (owner === epoch.current) setNote("Billing could not open. Try again.");
    } finally {
      if (owner === epoch.current) setBusy(false);
    }
  }
  async function startCheckout() {
    const owner=epoch.current;setBusy(true);setNote("");
    try{
      const result=await authenticated<{url?:string;ready?:boolean;processing?:boolean}>("checkout",flow.device?{userCode:flow.device}:{});
      if(owner!==epoch.current)return;
      if(result.ready||result.processing){setRefresh(value=>value+1);return;}
      if(!result.url?.startsWith('https://checkout.stripe.com/'))throw Error('checkout_unavailable');
      location.assign(result.url);
    }catch(error){if(owner===epoch.current)setNote(error instanceof Error&&error.message==='manage_subscription_required'?
      'Your existing subscription needs attention. Use Manage subscription below to update it.':
      'Checkout could not open. Try again; we’ll reuse your existing checkout.');}
    finally{if(owner===epoch.current)setBusy(false);}
  }
  const subscriptionActive = subscription?.linked === true && subscription.status === "active";
  async function connectDevice() {
    const owner = epoch.current;
    setBusy(true);
    setNote("");
    try {
      const result = await authenticated("device", { userCode: flow.device, history:historyApproval?.history===true });
      if (owner !== epoch.current) return;
      if (result.connected !== true) throw Error("approval_not_confirmed");
      onApprovalStateChange("approved");
      history.replaceState(
        history.state,
        "",
        location.pathname + location.search,
      );
      setNote("Computer connected. Return to your terminal to continue setup.");
      onConnected();
      try {
        const value = await authenticated<Subscription>("subscription");
        if (owner === epoch.current) setSubscription(value);
      } catch {
        /* Connection is already confirmed; a later refresh updates the list. */
      }
    } catch (error) {
      if (owner !== epoch.current) return;
      setNote(
        error instanceof Error && error.message === "collector_already_connected"
          ? "This computer already has a history connection. Your agent should reuse its private collector configuration; existing projects were not changed."
          : error instanceof Error && error.message === "device_limit"
          ? "All three computer slots are in use. Manage your computers to release a slot, then run phasedrift login again."
          : "Connection was not confirmed. Run phasedrift login again and check the code.",
      );
    } finally {
      if (owner === epoch.current) setBusy(false);
    }
  }
  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      setNote(label + " copied.");
    } catch {
      setNote(
        "Clipboard access was blocked. Select and copy the text instead.",
      );
    }
  }
  if (!subject || recovery)
    return (
      <AccountAuth
        getClient={getClient}
        email={email}
        purchase={Boolean(flow.purchase)}
        computerApproval={approvalState === "pending"}
        recovery={recovery}
        onPasswordSaved={onPasswordSaved}
      />
    );
  if (approvalState) {
    const approved = approvalState === "approved";
    const leaveApproval = (next: AppView) => {
      onApprovalStateChange(null);
      history.replaceState(history.state, "", location.pathname + location.search);
      setNote("");
      navigate(next);
    };
    return (
      <section className="computer-approval" aria-labelledby="computer-approval-title">
        <div className="approval-icon" aria-hidden="true">
          {approved ? <Check size={28} /> : <Monitor size={28} />}
        </div>
        <h1 id="computer-approval-title" ref={approvalHeading} tabIndex={-1}>
          {approved ? "Computer approved." : "Approve this computer."}
        </h1>
        <p className="approval-intro">
          {approved ? "You’re done here. Your agent can finish setup." : "Your agent is waiting for your approval."}
        </p>
        {!approved && (
          <>
            <div className="approval-code">
              <span>Match this code with your terminal</span>
              <strong>{flow.device}</strong>
            </div>
            <p className="approval-account">Connecting to <strong>{email || "your account"}</strong></p>
            <button className="primary-button approval-action" disabled={busy || !subscriptionActive || !historyApproval}
              aria-busy={busy} onClick={() => void connectDevice()}>
              {busy ? "Approving…" : subscription ? "Approve this computer" : note ? "Approval unavailable" : "Checking subscription…"}
            </button>
            {!subscriptionActive && subscription && (
              <p className="approval-recovery">
                An active subscription is needed before approving this computer.{" "}
                <button className="quiet-link" disabled={busy||subscription.checkout==='processing'} onClick={()=>void startCheckout()}>{subscription.checkout==='processing'?'Confirming payment…':'Subscribe to PhaseDrift'}</button>
              </p>
            )}
            <p className="approval-privacy">{historyApproval?.history
              ? "Connects this computer for project history. Project setup syncs agent-work summaries, including future measurements. Cloud code health comes from verified main baselines. Source code, transcripts and paid analysis stay off."
              : "Uses one of your three computer slots. Project history sync is off."}</p>
          </>
        )}
        {note && <p className="approval-note" role={approved ? "status" : "alert"}>{note}</p>}
        {approved ? (
          <button className="secondary-button" onClick={() => leaveApproval("projects")}>Go to projects <ArrowRight size={16} /></button>
        ) : (
          <div className="approval-secondary">
            <button className="quiet-link" disabled={busy} onClick={() => leaveApproval("projects")}>Not now</button>
            {note && !subscription && (
              <button className="quiet-link" onClick={() => setRefresh(value=>value+1)}>Try again</button>
            )}
            {note.includes("All three computer slots") && (
              <button className="quiet-link" disabled={busy} onClick={() => leaveApproval("computers")}>Manage computers</button>
            )}
          </div>
        )}
      </section>
    );
  }
  if (view !== "settings" && !subscription) {
    return (
      <section className="account-setup subscription-check" aria-live="polite" aria-busy={!note}>
        <div className="account-welcome"><div>
          <h1>{note ? "We couldn’t check your subscription." : "Checking your subscription…"}</h1>
          <p>{note || "We’ll show your next step when the check finishes."}</p>
        </div></div>
        {note && <button className="secondary-button" onClick={() => setRefresh(value=>value+1)}>Try again</button>}
      </section>
    );
  }
  if (view !== "settings" && !(view === "computers" && subscription?.linked) && subscription && !subscriptionActive) {
    return (
      <section className="account-setup subscription-gate" aria-labelledby="subscription-next-step">
        <div className="account-welcome"><div>
          <h1 id="subscription-next-step">{subscription.checkout==='processing' ? "Confirming your subscription…" : subscription.checkout==='open' ? "Finish your subscription." : subscription.linked ? "Your subscription is inactive." : "Choose your PhaseDrift plan."}</h1>
          <p>{subscription.checkout==='processing' ? "Project setup opens automatically when payment is confirmed." : subscription.checkout==='open' ? "Your checkout is saved. Continue when you’re ready." : subscription.linked
            ? "Your account is still here. Activate a subscription before connecting a project or computer."
            : "Your account is created. Choose a subscription before connecting your first project."}</p>
        </div></div>
        <ol className="onboarding-steps" aria-label="Setup progress">
          <li><Check size={18} aria-hidden="true" /> Account created</li>
          <li aria-current="step">{subscription.linked ? "Activate subscription" : "Choose a plan"}</li>
          <li>Connect your project</li>
        </ol>
        <section className="subscription-plan" aria-labelledby="subscription-plan-name">
          <div className="subscription-purchase">
            <h2 id="subscription-plan-name">PhaseDrift</h2>
            <div className="subscription-price" aria-label="$19 USD per month">
              <strong>$19</strong><span>USD / month</span>
            </div>
            <p className="subscription-billing">Billed monthly. Cancel anytime.</p>
            <div className="subscription-actions">
              {subscription.checkout!=='processing'&&<button className="primary-button" aria-label={busy?'Opening checkout…':subscription.checkout==='open'?'Continue checkout':'Subscribe for $19/month'} disabled={busy} onClick={()=>void startCheckout()}>{busy?'Opening checkout…':subscription.checkout==='open'?'Continue checkout':'Subscribe'} <ArrowRight size={16} /></button>}
              {subscription.checkout==='processing'&&<p role="status">Confirming payment…</p>}
              {subscription.linked&&<button className="secondary-button" disabled={busy} onClick={()=>void manageBilling()}>Manage subscription</button>}
              {note&&<button className="secondary-button" disabled={busy} onClick={()=>setRefresh(value=>value+1)}>Try again</button>}
            </div>
            <p className="subscription-terms">Plus applicable tax. Secure Stripe checkout.</p>
          </div>
          <div className="subscription-inclusions">
            <ul className="subscription-features" aria-label="Included features">
              <li><Check size={17} aria-hidden="true" /><span>Unlimited projects · Up to 3 computers</span></li>
              <li><Check size={17} aria-hidden="true" /><span>Code health and agent-work history</span></li>
              <li><Check size={17} aria-hidden="true" /><span>Workflow timings and improvement tracking</span></li>
              <li><Check size={17} aria-hidden="true" /><span>Cloud dashboard for metric results</span></li>
              <li><Check size={17} aria-hidden="true" /><span>All product updates</span></li>
            </ul>
            <div className="subscription-allowance">
              <h3>Included report analysis</h3>
              <ul>
                <li><strong>10,000 attempts/month</strong> · 1,000/day</li>
                <li><strong>10 million input tokens/month</strong></li>
                <li>Optional. No overage charges.</li>
              </ul>
              <p>UTC monthly limits. Local collection and scans don’t count.</p>
            </div>
          </div>
        </section>
        <p className="subscription-data-note">Source code and transcripts stay local.</p>
        {note && <p role="alert">{note}</p>}
      </section>
    );
  }
  return (
    <section
      className="account-setup"
      aria-label="PhaseDrift account and setup"
    >
      <>
        {releaseComputer && <ConfirmAction title={`Release ${releaseComputer.name}?`} action="Release computer" onCancel={() => setReleaseComputer(null)} onConfirm={releaseSelectedComputer}>
          <p>This computer will lose license and cloud access for new online requests. Saved history and local files remain. You can connect it again later.</p>
        </ConfirmAction>}
        {view !== "projects" && (
          <div className="account-welcome">
            <div>
              <h1>
                {
                  {
                    setup: "Set up a project.",
                    computers: "Your computers.",
                    billing: "Your subscription.",
                    settings: "Account settings.",
                  }[view]
                }
              </h1>
              <p>
                {
                  {
                    setup: "One prompt. Your agent handles the setup.",
                    computers:
                      "Manage the computers connected to your subscription.",
                    billing: "Your plan, payment details and license.",
                    settings: "Manage your account and cloud history.",
                  }[view]
                }
              </p>
            </div>
          </div>
        )}
        {view === "setup" && (
            <div id="setup" className="agent-setup">
              <div>
                <h2>Let your agent set it up.</h2>
                <p>
                  Install the local application once, then keep working. Copy the
                  prompt into your coding agent with your project open.
                </p>
              </div>
              <button
                className="primary-button"
                onClick={() => void copy(agentSetupPrompt, "Setup prompt")}
              >
                Copy setup prompt
              </button>
              <ol className="setup-outcomes">
                <li>Install the local app</li>
                <li>Connect your computer</li>
                <li>Open the browser dashboard</li>
              </ol>
              <dl className="setup-locations" aria-label="Where PhaseDrift runs">
                <div><dt>On this computer</dt><dd>The tracker, code analyzers and your project dashboards. Code and detailed agent activity stay here.</dd></div>
                <div><dt>In your account</dt><dd>Billing, connected computers and setup. Project history appears here when you choose to sync it.</dd></div>
              </dl>
              <p className="setup-privacy">
                You approve sign-in in your browser. Your agent never needs your
                license key.
              </p>
              <details className="full-prompt">
                <summary>Read the full setup prompt</summary>
                <pre tabIndex={0}>{agentSetupPrompt}</pre>
              </details>
            </div>
          )}
        {view === "computers" && subscription?.linked && (
          <section id="computers" className="account-computers">
            <h2>Connected computers</h2>
            <p>
              Up to three computers share this subscription. Connect a new one
              with <code>phasedrift login</code>.
            </p>
            {subscription.machines?.devices ? (
              subscription.machines.devices.filter((item) => !item.revoked)
                .length === 0 ? (
                <div className="computer-empty">
                  <Monitor size={32} />
                  <h3>Connect your first computer.</h3>
                  <p>
                    Your agent will install PhaseDrift and open the browser for
                    your approval.
                  </p>
                  <button
                    className="primary-button"
                    onClick={() => navigate("setup")}
                  >
                    Set up a project <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <ul>
                  {subscription.machines.devices
                    .filter((item) => !item.revoked)
                    .map((item, index) => (
                      <li key={item.device_id}>
                        <div className="computer-identity"><EditableName value={item.label || `Computer ${index + 1}`} label={item.label || `computer ${index + 1}`} onSave={name => renameComputer(item.device_id,name)} /><small>{item.device_id.slice(0,8)}</small></div>
                        <button type="button" className="secondary-button" disabled={busy}
                          onClick={() => setReleaseComputer({id:item.device_id,name:item.label || `Computer ${index+1}`})}>Release computer</button>

                      </li>
                    ))}
                </ul>
              )
            ) : (
              <p>
                Your computer list is not available yet. Connect your first
                computer to continue.
              </p>
            )}
          </section>
        )}
        {view === "billing" && subscription?.linked && (
          <div className="billing-plan">
            <div>
              <span>PhaseDrift</span>
              <h2>
                {subscription.status === "active"
                  ? "Subscription active"
                  : "Subscription inactive"}
              </h2>
              <p>
                Local tracking, code health, managed report analysis and cloud
                history.
              </p>
            </div>
            <button
              className="primary-button"
              disabled={busy}
              onClick={() => void manageBilling()}
            >
              Manage billing
            </button>
            <p className="billing-footnote">
              Update payment details, view invoices or manage your subscription
              securely through Stripe.
            </p>
          </div>
        )}
        {view === "settings" && (
          <section className="account-profile">
            <h2>Your account</h2>
            <dl>
              <dt>Email</dt>
              <dd>{email}</dd>
            </dl>
            <h3>Password</h3>
            <p>
              Set or reset your password with a private link sent to your
              account email.
            </p>
            <button
              className="secondary-button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void getClient()
                  .then((client) =>
                    client.auth.resetPasswordForEmail(email, {
                      redirectTo: location.origin + "/?view=password",
                    }),
                  )
                  .then(({ error }) => {
                    if (error) throw error;
                    setNote("Check your inbox for your password link.");
                  })
                  .catch(() =>
                    setNote(
                      "We couldn’t send your password link. Try again in a minute.",
                    ),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              Send password link
            </button>
          </section>
        )}
        {view === "billing" && subscription?.licenseKey && (
          <details className="manual-license">
            <summary>License key for manual setup</summary>
            <div className="manual-key">
              <input
                aria-label="Your private license key"
                readOnly
                type={visible ? "text" : "password"}
                value={subscription.licenseKey ?? ""}
                autoComplete="off"
              />
              <button
                className="secondary-button"
                onClick={() =>
                  void copy(subscription.licenseKey ?? "", "License key")
                }
              >
                Copy key
              </button>
              <button
                className="secondary-button"
                aria-pressed={visible}
                onClick={() => setVisible(!visible)}
              >
                {visible ? "Hide key" : "Show key"}
              </button>
            </div>
          </details>
        )}
      </>
      <p className="account-note" role="status" aria-live="polite">
        {note}
      </p>
    </section>
  );
}
